import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Container, Table, Button, Row, Col, Dropdown } from 'react-bootstrap';
import { UploadModal, CommentModal } from './components/modal/modal';
import ReportsService from '../../../../../../services/reports-service';

const IssuerAdhocReportContainer = ({ filteredReports, handleCheckboxChange, handleSendReport, handleReject }) => {
  // Instantiate the report service.
  const reportService = useMemo(() => new ReportsService(), []);

  // State to control the Upload modal.
  const [uploadModalData, setUploadModalData] = useState({
    show: false,
    report: null,
    item: null,
  });

  // State to control the Comment modal.
  const [commentModalData, setCommentModalData] = useState({
    show: false,
    comment: '',
  });

  // Local state to track selected report IDs (using the report's unique id)
  const [selectedItems, setSelectedItems] = useState([]);

  // State and ref for dropdown functionality
  const [expandedItem, setExpandedItem] = useState(null);
  const dropdownRefs = useRef([]);

  const handleUploadClick = useCallback((item) => {
    setUploadModalData({ show: true, report: null, item });
  }, []);

  const handleUploadModalClose = useCallback(() => {
    setUploadModalData({ show: false, report: null, item: null });
  }, []);

  // Local handler using report.id instead of index
  const handleLocalCheckboxChange = (id) => (e) => {
    if (e.target.checked) {
      setSelectedItems((prev) => [...prev, id]);
    } else {
      setSelectedItems((prev) => prev.filter((itemId) => itemId !== id));
    }
    if (handleCheckboxChange) {
      handleCheckboxChange(id);
    }
  };

  // Handler for opening the comment modal
  const handleCommentClick = (comment) => {
    setCommentModalData({ show: true, comment });
  };

  const handleCommentModalClose = () => {
    setCommentModalData({ show: false, comment: '' });
  };

  // API call for previewing a report.
  const handlePreviewClick = async (item) => {
    try {
      const options = {};
      if (item.bpId) options.bpId = item.bpId;
      if (item.taxAdjustmentType) options.taxAdjustmentType = item.taxAdjustmentType;

      const response = await reportService.retrieveIssuerAdhocReportFile(
        item.reportType,
        item.isin,
        item.reportTitle,
        item.paymentDate,
        options
      );

      if (response && response.fileURL) {
        const newWindow = window.open(response.fileURL, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
      } else {
        console.error('Preview: No file URL returned');
      }
    } catch (error) {
      console.error('Error fetching report preview:', error.message);
    }
  };

  // API call for downloading a report.
  const handleDownloadClick = async (item) => {
    try {
      const options = {};
      if (item.bpId) options.bpId = item.bpId;
      if (item.taxAdjustmentType) options.taxAdjustmentType = item.taxAdjustmentType;

      const response = await reportService.retrieveIssuerAdhocReportFile(
        item.reportType,
        item.isin,
        item.reportTitle,
        item.paymentDate,
        options
      );

      if (response && response.fileURL) {
        const url = response.fileURL;
        const filename = response.fileName || url.substring(url.lastIndexOf('/') + 1);

        const fileResponse = await fetch(url);
        if (!fileResponse.ok) throw new Error('Network response was not ok');

        const blob = await fileResponse.blob();
        const objectUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      } else {
        console.error('Download: No file URL returned');
      }
    } catch (error) {
      console.error('Error fetching report download:', error);
    }
  };

  // Toggle dropdown for a given row index.
  const handleDropdownToggle = (index) => {
    setExpandedItem((prev) => (prev === index ? null : index));
  };

  // Close dropdown if clicking outside.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRefs.current.some(ref => ref && ref.contains(event.target))) return;
      if (document.getElementById('dropdown-menu') && document.getElementById('dropdown-menu').contains(event.target)) return;
      setExpandedItem(null);
    };

    if (expandedItem !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expandedItem]);

  // Render status cell with dropdown that includes Preview and Download actions.
  // If the status is "For Checking", include an Upload option in the dropdown.
  const renderStatusCell = (item, index) => {
    if (item.status === 'Report Unavailable') {
      return (
        <Button variant="primary" onClick={() => handleUploadClick(item)}>
          Upload
        </Button>
      );
    } else {
      return (
        <div className="status-container" ref={el => dropdownRefs.current[index] = el}>
          <span className="status-text">{item.status}</span>
          <Dropdown show={expandedItem === index}>
            <Dropdown.Toggle
              variant="link"
              className="dropdown-arrow-button"
              onClick={() => handleDropdownToggle(index)}
            >
              ▼
            </Dropdown.Toggle>
            {expandedItem === index &&
              ReactDOM.createPortal(
                <div className="dropdown-overlay">
                  <Dropdown.Menu className="dropdown-menu" id="dropdown-menu">
                    <Dropdown.Item onClick={() => { handlePreviewClick(item); setExpandedItem(null); }}>
                      Preview
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => { handleDownloadClick(item); setExpandedItem(null); }}>
                      Download
                    </Dropdown.Item>
                    {item.status === 'For Checking' && (
                      <Dropdown.Item onClick={() => { handleUploadClick(item); setExpandedItem(null); }}>
                        Upload
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </div>,
                document.body
              )
            }
          </Dropdown>
        </div>
      );
    }
  };

  return (
    <Container
      fluid
      className="issuer-adhoc-report-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '35.5rem',
      }}
    >
      {/* Table wrapper that grows and scrolls if needed */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <Table bordered>
          <thead>
            <tr>
              <th>REPORT NAME</th>
              <th>ISSUER</th>
              <th>BPID</th>
              <th>SECURITY NAME</th>
              <th>ISIN</th>
              <th>REQUESTED DATE</th>
              <th>Refund Amount</th>
              <th>STATUS</th>
            </tr>
          </thead>
          <tbody>
            {filteredReports && filteredReports.length > 0 ? (
              filteredReports.map((item, index) => (
                <tr key={item.id || index}>
                  <td>
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        style={{ marginRight: '8px' }}
                        disabled={item.status !== 'For Checking'}
                        checked={selectedItems.includes(item.id)}
                        onChange={handleLocalCheckboxChange(item.id)}
                      />
                      {item.reportTitle}
                      {item.taxAdjustmentType && ` (${item.taxAdjustmentType})`}
                    </label>
                  </td>
                  <td>{item.securityIssuer}</td>
                  <td>{item.bpId}</td>
                  <td>
                    <span>{item.securityName}</span>
                    {item.comment && item.comment.trim() !== '' && (
                      <span
                        style={{
                          cursor: 'pointer',
                          marginLeft: '5px',
                        }}
                        onClick={() => handleCommentClick(item.comment)}
                      >
                        &#9865;
                      </span>
                    )}
                  </td>
                  <td>{item.isin}</td>
                  <td>{item.issuerRequestDate}</td>
                  <td>{item.amount || ''}</td>
                  <td>
                    {renderStatusCell(item, index)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center">
                  No Issuer Adhoc Reports Available.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Row with "Reject Report" and "Send Report" Buttons */}
      <Row className="mt-3">
        <Col className="d-flex justify-content-end">
          <Button variant="danger" className="mr-2" onClick={handleReject}>
            Reject Report
          </Button>
          <Button variant="primary" onClick={handleSendReport}>
            Send Report
          </Button>
        </Col>
      </Row>

      <UploadModal
        report={uploadModalData.report}
        item={uploadModalData.item}
        show={uploadModalData.show}
        onClose={handleUploadModalClose}
      />
      <CommentModal
        show={commentModalData.show}
        comment={commentModalData.comment}
        onClose={handleCommentModalClose}
      />
    </Container>
  );
};

export default IssuerAdhocReportContainer;
